import { buildCrackmapRenderer } from '../crackmaps/buildCrackmapRenderer';
import { CRACKMAP_WFS_PROPERTY_NAMES } from '../crackmaps/crackmapWfs';
import type { ArcGisClassBreaksRendererJson } from '../map/buildDefectRulesRenderer';
import type { DefectGeometryKind } from '../map/defectGeometry';
import { buildLineConditionRenderer } from '../lineConditions/buildLineConditionRenderer';
import { LINE_CONDITION_WFS_PROPERTY_NAMES } from '../lineConditions/lineConditionCatalog';
import { buildPas2161Renderer } from '../pas2161/buildPas2161Renderer';
import { PAS2161_WFS_PROPERTY_NAMES } from '../pas2161/pas2161Bands';
import { resolvePas2161OverlayLabels } from '../pas2161/pas2161LayerModel';
import { buildPotholeRenderer } from '../potholes/buildPotholeRenderer';
import { POTHOLE_WFS_PROPERTY_NAMES } from '../potholes/potholeCatalog';
import type { DefectOverlayLayerKind } from './overlayKinds';
import { isDefectOverlayLayerKind } from './overlayKinds';

export interface DefectOverlayProfile {
  readonly layerKind: DefectOverlayLayerKind;
  readonly propertyNames: string;
  readonly geometryKind: DefectGeometryKind;
  readonly loadingLabel: string;
  readonly overviewLoadingLabel: string;
  readonly buildRenderer: (
    lineWidth?: number,
    lineStyle?: string,
    zoom?: number,
  ) => ArcGisClassBreaksRendererJson;
}

export interface ResolveDefectOverlayProfileOptions {
  readonly sourceLayerId?: string | null;
}

const PROFILES: Readonly<Record<DefectOverlayLayerKind, DefectOverlayProfile>> = {
  crackmap: {
    layerKind: 'crackmap',
    propertyNames: CRACKMAP_WFS_PROPERTY_NAMES,
    geometryKind: 'polyline',
    loadingLabel: 'Cracks',
    overviewLoadingLabel: 'Crack Grid',
    buildRenderer: buildCrackmapRenderer,
  },
  pothole: {
    layerKind: 'pothole',
    propertyNames: POTHOLE_WFS_PROPERTY_NAMES,
    geometryKind: 'polygon',
    loadingLabel: 'Potholes',
    overviewLoadingLabel: 'Pothole Grid',
    buildRenderer: (lineWidth) => buildPotholeRenderer(lineWidth),
  },
  'line-condition': {
    layerKind: 'line-condition',
    propertyNames: LINE_CONDITION_WFS_PROPERTY_NAMES,
    geometryKind: 'polyline',
    loadingLabel: 'Line Condition',
    overviewLoadingLabel: 'Line Condition Grid',
    buildRenderer: buildLineConditionRenderer,
  },
};

export function resolveDefectOverlayProfile(
  layerKind: string | null | undefined,
  options?: ResolveDefectOverlayProfileOptions,
): DefectOverlayProfile | null {
  if (!isDefectOverlayLayerKind(layerKind)) {
    return null;
  }
  const base = PROFILES[layerKind];
  const pas2161Labels = resolvePas2161OverlayLabels(options?.sourceLayerId);
  if (!pas2161Labels) {
    return base;
  }
  return {
    ...base,
    propertyNames: PAS2161_WFS_PROPERTY_NAMES,
    geometryKind: 'point',
    buildRenderer: (lineWidth, _lineStyle, zoom) =>
      buildPas2161Renderer(lineWidth, zoom),
    loadingLabel: pas2161Labels.loadingLabel,
    overviewLoadingLabel: pas2161Labels.overviewLoadingLabel,
  };
}

/** User-facing street-level vector loading banner. */
export function formatDefectOverlayLoadingMessage(loadingLabel: string): string {
  return `Loading ${loadingLabel.trim() || 'overlay'}…`;
}

/** User-facing overview grid loading banner. */
export function formatDefectOverviewLoadingMessage(
  overviewLoadingLabel: string,
): string {
  return `Loading ${overviewLoadingLabel.trim() || 'overlay grid'}…`;
}

/** User-facing overlay load failure title for the map loading banner. */
export function formatDefectOverlayLoadFailure(
  loadingLabel: string,
  _detail?: string | null,
): string {
  const label = loadingLabel.trim() || 'overlay';
  return `Unable to load ${label}`;
}
